import { useTranslation } from 'react-i18next';
import { Ban, Hourglass, LogOut, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { useAuthStore } from '@/stores/auth-store';
import { PartnerContactLine } from './PartnerContactPage';

/** What a signed-in partner sees while the portal refuses it: PART-423 pending approval, PART-424 blocked or cancelled. */
export function PartnerPendingPage({ blocked = false }: { blocked?: boolean }) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);
  const username = useAuthStore((s) => s.user?.username);
  const Icon = blocked ? Ban : Hourglass;

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/30">
      <div className="flex justify-end gap-1 p-4">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <main className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid={blocked ? 'partner-blocked' : 'partner-pending'}>
          <CardHeader className="items-center text-center">
            <Icon className="mb-2 h-10 w-10 text-muted-foreground" aria-hidden />
            <CardTitle>{t(blocked ? 'partnerPortal.pending.blockedTitle' : 'partnerPortal.pending.title')}</CardTitle>
            <CardDescription>
              {t(blocked ? 'partnerPortal.pending.blockedBody' : 'partnerPortal.pending.body')}
            </CardDescription>
          </CardHeader>
          {username ? (
            <CardContent className="text-center text-sm text-muted-foreground">
              {t('partnerPortal.pending.signedInAs', { username })}
            </CardContent>
          ) : null}
          {blocked ? null : (
            <CardContent>
              <section aria-labelledby="partner-pending-contact" className="space-y-2 rounded-md border p-4 text-center">
                <h4 id="partner-pending-contact" className="flex items-center justify-center gap-2 text-sm font-medium">
                  <MessageCircle className="h-4 w-4" aria-hidden />
                  {t('partnerPortal.contact.line')}
                </h4>
                <p className="text-sm text-muted-foreground">{t('partnerPortal.contact.description')}</p>
                <PartnerContactLine />
              </section>
            </CardContent>
          )}
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => void logout()}>
              <LogOut className="mr-2 h-4 w-4" aria-hidden />
              {t('auth.signOut')}
            </Button>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}
